import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { AppRoutes } from '@/constants/appRoutes'
import { EUserStatus } from '@/constants/userStatus'
import { useAppDispatch } from '@/hooks/redux'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { api } from '@/store/api'
import { setSession, setUserID } from '@/store/api/Auth/Auth.slice'
import { setUserCredentials } from '@/store/api/SignUp/SignUp.slice'
import { identifyUser } from '@/utils/analytics/analytics'
import { AuthMethods } from '@/utils/analytics/analyticsConstants'

export const useSSO = () => {
  const { t } = useTranslation()
  const baseUrl = import.meta.env.VITE_BASE_API_URL

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const startGoogleSSO = () => {
    try {
      const googleOAuthUrl = `${baseUrl}/api/v1/auth/google`
      window.location.href = googleOAuthUrl
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.error'))
    }
  }

  const successSSO = (
    token: string,
    status: string,
    email: string,
    id: string | null,
  ) => {
    try {
      const normalizedEmail = email.toLowerCase()

      dispatch(api.util.resetApiState())
      const sessionData = {
        token,
        id,
        userStatus: status,
      }

      dispatch(setSession(sessionData))

      // This identify user is for signup and singin flow via SSO
      identifyUser(normalizedEmail, {
        user_id: id,
        email: normalizedEmail,
        user_status: status,
        method: AuthMethods.GOOGLE,
      })

      if (id && email) {
        // SignUp flow via Google SSO

        dispatch(setUserCredentials({ normalizedEmail }))
        dispatch(setUserID(id))

        navigate(AppRoutes.welcomeToSelfCheck)
      } else {
        // Login flow via Google SSO

        if (status === EUserStatus.ACTIVE) {
          navigate(AppRoutes.dashboard)
        } else if (status === EUserStatus.PENDING_MOTIVATION) {
          navigate(AppRoutes.welcomeToSelfCheck)
        } else if (status === EUserStatus.PENDING_SET_PERSONAL_INFO) {
          navigate(AppRoutes.personalInfo)
        } else if (status === EUserStatus.PENDING_SOCIAL_MEDIAS) {
          navigate(AppRoutes.setupDigitalFootprint)
        } else if (status === EUserStatus.PENDING_ACCOUNT_ACTIVATION) {
          navigate(AppRoutes.defaultBehaviorAndTopics)
        }
      }
    } catch (error) {
      catchWithSentry(error)
      toast.error('Authentication failed. Please try again.')
      navigate(AppRoutes.login)
    }
  }

  return { startGoogleSSO, successSSO }
}
